<template>
  <div class="stat-card">
    <div class="card-header">
      <div class="card-title-section">
        <div class="card-title">{{ title }}</div>
        <div class="stat-icon" :class="iconClass">
          <i class="material-icons">{{ icon }}</i>
        </div>
      </div>
    </div>
    
    <div class="card-body">
      <div class="stat-value">
        {{ value }}
      </div>
      
      <div class="stat-trend" 
          :class="{
              'positive': trend === 'positive',
              'negative': trend === 'negative',
              'neutral': trend === 'neutral'
          }">
        <div class="trend-indicator">
          <i class="material-icons">{{ trendIcon }}</i>
          <span class="trend-percentage">{{ formattedPercentage }}%</span>
        </div>
        <span class="trend-period">since {{ periodText }}</span>
      </div>
    </div>
    
    <!-- Animated background elements -->
    <div class="card-decoration">
      <div class="decoration-circle decoration-circle-1"></div>
      <div class="decoration-circle decoration-circle-2"></div>
    </div>
  </div>
</template>

<script>
export default {
  name: "StatCard",
  props: {
    title: {
      type: String,
      required: true
    },
    value: {
      type: String,
      required: true
    },
    icon: {
      type: String,
      required: true
    },
    iconClass: {
      type: String,
      required: true
    },
    trend: {
      type: String,
      default: 'neutral',
      validator: value => ['positive', 'negative', 'neutral'].includes(value)
    },
    percentage: {
      type: [Number, String],
      default: 0
    },
    comparisonPeriod: {
      type: String,
      default: 'previous_period'
    }
  },
  computed: {
    trendIcon() {
      if (this.trend === 'positive') return 'trending_up';
      if (this.trend === 'negative') return 'trending_down';
      return 'trending_flat';
    },
    periodText() {
      return this.comparisonPeriod === 'previous_period' ? 'previous period' : 'last month';
    },
    formattedPercentage() {
      const num = typeof this.percentage === 'string' ? parseFloat(this.percentage) : this.percentage;
      return Math.abs(num).toFixed(1);
    }
  }
};
</script>

<style scoped lang="scss">
@import "@/assets/variables.scss";

.stat-card {
  /* Flat surface for consistent elevation */
  background: $lightCardBg;
  border-radius: $cardRadius;
  padding: $cardPaddingY $cardPaddingX;
  box-shadow: none;
  border: 1px solid $lightBorderColor;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;
  backdrop-filter: none;
  min-height: $kpiCardMinHeight; /* Uniform KPI height */
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 2px;
    background: linear-gradient(90deg, transparent 0%, $primaryColor 50%, transparent 100%);
    opacity: 0;
    transition: opacity 0.3s ease;
  }
  
  &:hover {
    transform: translateY(-1px);
    
    &::before { opacity: 1; }
    .stat-icon { transform: scale(1.03); }
    .decoration-circle { animation-play-state: running; }
  }
  
  .card-header {
    padding: 0;
  }
  
  .card-title-section {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }
  
  .card-title {
    font-size: 0.78rem;
    color: $textColor;
    font-weight: 500;
    opacity: 0.8;
  }
  
  .stat-icon {
    width: 36px;
    height: 36px;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
    
    i {
      font-size: 18px;
      color: white;
      text-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
    }
    
    &.product-icon { background: linear-gradient(135deg, $infoColor 0%, darken($infoColor, 10%) 100%); box-shadow: 0 2px 8px rgba($infoColor, 0.3); }
    &.customer-icon { background: linear-gradient(135deg, $successColor 0%, darken($successColor, 10%) 100%); box-shadow: 0 2px 8px rgba($successColor, 0.3); }
    &.order-icon { background: linear-gradient(135deg, $warningColor 0%, darken($warningColor, 10%) 100%); box-shadow: 0 2px 8px rgba($warningColor, 0.3); }
    &.aov-icon { background: linear-gradient(135deg, $secondaryColor 0%, darken($secondaryColor, 10%) 100%); box-shadow: 0 2px 8px rgba($secondaryColor, 0.3); }
    &.revenue-icon { background: linear-gradient(135deg, $dangerColor 0%, darken($dangerColor, 10%) 100%); box-shadow: 0 2px 8px rgba($dangerColor, 0.3); }
  }
  
  .card-body { padding: 10px 0 0 0; }
  
  .stat-value {
    font-size: 1.6rem;
    font-weight: 800;
    margin: 6px 0 8px 0;
    color: $primaryColor;
    line-height: 1.2;
    letter-spacing: -0.3px;
  }
  
  .stat-trend {
    display: flex;
    align-items: center;
    font-size: 0.75rem;
    font-weight: 500;
    padding: 6px 10px;
    border-radius: 6px;
    background: rgba(255, 255, 255, 0.5);
    backdrop-filter: blur(5px);
    
    .trend-indicator {
      display: flex;
      align-items: center;
      margin-right: 6px;
      
      i { font-size: 1rem; margin-right: 3px; }
      .trend-percentage { font-weight: 600; }
    }
    
    .trend-period { color: $textColor; opacity: 0.7; font-size: 0.7rem; }
    
    &.positive { color: $successColor; background: rgba($successColor, 0.1); border: 1px solid rgba($successColor, 0.2); .trend-indicator i { color: $successColor; } }
    &.negative { color: $dangerColor; background: rgba($dangerColor, 0.1); border: 1px solid rgba($dangerColor, 0.2); .trend-indicator i { color: $dangerColor; } }
    &.neutral  { color: $textColor; opacity: 0.8; background: rgba($textColor, 0.05); border: 1px solid rgba($textColor, 0.1); .trend-indicator i { color: $textColor; opacity: 0.6; } }
  }
  
  :global(.dark) & {
    background: $darkCardBg;
    border-color: $darkBorderColor;
    box-shadow: none;
    
    &:hover { transform: translateY(-1px); }
    
    .card-title { color: $darkTextColor; opacity: 0.9; }
    .stat-value { color: $secondaryColorDark; }
    
    .stat-trend {
      .trend-period { color: $darkTextColor; opacity: 0.7; }
      &.neutral  { color: $darkTextColor; opacity: 0.8; }
      &.positive { color: $successColorDark; }
      &.negative { color: $dangerColorDark; }
    }
  }
}

.card-decoration {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  
  .decoration-circle {
    position: absolute;
    border-radius: 50%;
    background-color: rgba(255, 255, 255, 0.08);
    animation: pulse 6s infinite;
    animation-play-state: paused;
    
    &.decoration-circle-1 { width: 60px; height: 60px; top: -15px; left: -15px; }
    &.decoration-circle-2 { width: 80px; height: 80px; bottom: -25px; right: -25px; }
  }
}

@keyframes pulse {
  0% { transform: scale(0.5); opacity: 0.3; }
  50% { transform: scale(1); opacity: 0.6; }
  100% { transform: scale(0.5); opacity: 0.3; }
}
</style>
